import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';

export interface DirectoryPayer {
  id: string;
  name: string;
  aliases: string[];
  stateEntity: string | null;
  payerType: string;
  providerLine: string | null;
  verificationStrategy: string;
  status: string;
}

// The directory list row — same fields as DirectoryPayer plus each payer's
// version status, so the list itself shows what's charted (mirrors
// LocationPayerRow's activeVersion/proposedVersion/effectiveStrategy).
export interface DirectoryPayerSummary extends DirectoryPayer {
  activeVersion: number | null;
  proposedVersion: number | null;
  effectiveStrategy: 'voice' | 'staff' | 'blocked';
}

// Mirrors backend charting/schemas.ts IvrGraph — the assembled navigation
// map. `outcome` is only set on a state whose kind is 'terminal'.
export interface IvrState {
  id: string;
  prompt: string;
  kind: 'menu' | 'data_entry' | 'announcement' | 'terminal';
  outcome?:
    | 'queue_reached'
    | 'human_answered'
    | 'subscriber_data_required'
    | 'representative_unavailable';
}

export interface IvrTransition {
  fromState: string;
  toState?: string;
  action: 'press' | 'key_variable' | 'key_literal' | 'say' | 'listen_silent' | 'confirm_then_press';
  value?: string;
  terminator?: string;
  confirmed: boolean;
  observedCount: number;
  lastObservedAt?: string;
  onFail?: string;
  note?: string;
}

export interface IvrGraph {
  payer: string;
  version: number;
  charted_date: string;
  source: string;
  entryState: string;
  states: IvrState[];
  transitions: IvrTransition[];
}

export type PhoneMapStatus = 'pending' | 'not_charted' | 'proposed' | 'active';

export interface LocationPayerRow {
  id: string;
  payerId: string;
  payerName: string;
  phoneMapStatus: PhoneMapStatus;
  activeVersion: number | null;
  proposedVersion: number | null;
  providerLine: string | null;
  hasOverride: boolean;
  effectiveStrategy: 'voice' | 'staff' | 'blocked';
}

// Mirrors backend charting/schemas.ts Diff/KnowledgeDiff — what changed vs.
// the active version at the time this one was synthesized.
export interface NavigationDiff {
  statesAdded: string[];
  transitionsAdded: string[];
  transitionsRemoved: string[];
  transitionsChanged: { description: string; before: string; after: string }[];
  notes: string[];
}

export interface KnowledgeFieldDiff {
  fieldsChanged: { field: string; before: string; after: string }[];
}

export interface VersionDiffSummary {
  navigation: NavigationDiff | null;
  knowledge: KnowledgeFieldDiff | null;
}

export interface PayerMapVersion {
  id: string;
  version: number;
  status: 'proposed' | 'active' | 'superseded';
  createdAt: string;
  activatedAt: string | null;
  diffSummary: VersionDiffSummary | null;
}

// Operational facts about a payer, computed from charting evidence — lives
// alongside the navigation map in the same InsurancePayerMap row/version
// (see backend charting/schemas.ts PayerKnowledgeSchema).
export interface PayerKnowledge {
  version: number;
  authentication: {
    providerNpiRequired: boolean;
    providerTinRequired: boolean;
    providerLicenseRequired: boolean;
    memberIdRequired: boolean;
    memberDobRequired: boolean;
    officeZipRequired: boolean;
    providerCredentialsBeforeMemberInfo: boolean | null;
  };
  representative: {
    everReached: boolean;
    callbackEverOffered: boolean;
  };
  behavior: {
    neverPress0: boolean;
    // null = no charting run has ever reported this either way yet.
    speechSupported: boolean | null;
    estimatedWaitStated: string | null;
  };
}

export interface PayerMapBundle {
  versions: PayerMapVersion[];
  active:
    | (PayerMapVersion & {
        map: IvrGraph;
        knowledge: PayerKnowledge;
        renderedText: string;
        approvedBy: string | null;
      })
    | null;
}

// Full detail for a single version, including ones that aren't active yet —
// the payer detail page shows this for whichever version is selected.
export interface MapVersionDetail extends PayerMapVersion {
  payerId: string;
  map: IvrGraph;
  knowledge: PayerKnowledge;
  renderedText: string;
  approvedBy: string | null;
  sourceRunIds: string[];
}

export interface PayerDirectoryPatch {
  name?: string;
  aliases?: string[];
  stateEntity?: string | null;
  payerType?: 'commercial' | 'medicaid' | 'fedvip';
  providerLine?: string | null;
  verificationStrategy?: 'voice' | 'staff' | 'blocked';
  status?: 'active' | 'pending' | 'flagged' | 'blocked';
}

export interface PayerDeleteImpact {
  locationCount: number;
  chartingRunCount: number;
  mapVersionCount: number;
}

export interface AgencyLocation {
  id: string;
  name: string;
  practiceName: string;
}

export interface TriggerChartingResult {
  callId: string;
  chartingRunId: string;
}

// Pasted in as raw JSON via RunChartingSection's "Add Subscriber Info"
// modal, never persisted — see backend payers.service.ts
// ChartingSubscriberInput. Free-form rather than a fixed shape, since it's
// meant to accept whatever subscriber_* fields the pasted JSON has (e.g.
// subscriber_id, subscriber_dob_digits, subscriber_name). Only needed for
// payers whose IVR requires a real patient's identity to get past the
// "enter member ID/DOB" gate (PayerKnowledge.authentication.memberIdRequired
// / memberDobRequired).
export type ChartingSubscriberInput = Record<string, string>;

// Full detail for one charting call, including its transcript — fetched
// lazily when a row in the payer detail page's "Charting calls" log expands.
export interface ChartingRunDetail {
  id: string;
  terminalState: string;
  assemblyStatus: 'ok' | 'partial' | 'failed';
  assemblyNotes: string | null;
  renderedText: string | null;
  transcript: string | null;
  recordingUrl: string | null;
  createdAt: string;
}

// Lightweight row for the payer detail page's "Charting calls" log — full
// detail (transcript included) is fetched lazily per row via getChartingRun.
export interface ChartingRunSummary {
  id: string;
  chartingRunId: string;
  kind: string;
  terminalState: string;
  assemblyStatus: 'ok' | 'partial' | 'failed';
  assemblyNotes: string | null;
  createdAt: string;
}

export const payersApi = {
  searchDirectory: (q: string) =>
    apiClient
      .get<ApiResponse<DirectoryPayer[]>>('/insurance/payers/directory', { params: { q } })
      .then((r) => r.data.data),

  addPayerManually: (name: string) =>
    apiClient
      .post<ApiResponse<DirectoryPayer>>('/insurance/payers/directory', { name })
      .then((r) => r.data.data),

  listLocationPayers: (locationId: string) =>
    apiClient
      .get<ApiResponse<LocationPayerRow[]>>('/insurance/payers', { params: { locationId } })
      .then((r) => r.data.data),

  addPayerToLocation: (locationId: string, payerId: string) =>
    apiClient
      .post<ApiResponse<LocationPayerRow>>('/insurance/payers', { locationId, payerId })
      .then((r) => r.data.data),

  // Removes this location's selection only — the directory entry itself is
  // untouched (see deletePayer for removing it from the agency directory).
  removeLocationPayer: (locationPayerId: string, locationId: string) =>
    apiClient
      .delete<ApiResponse<{ deleted: boolean }>>(`/insurance/payers/${locationPayerId}`, {
        params: { locationId },
      })
      .then((r) => r.data.data),

  setProviderLineOverride: (
    locationPayerId: string,
    locationId: string,
    providerLineOverride: string | null,
  ) =>
    apiClient
      .patch<ApiResponse<LocationPayerRow>>(`/insurance/payers/${locationPayerId}`, {
        locationId,
        providerLineOverride,
      })
      .then((r) => r.data.data),

  getPayerMap: (payerId: string) =>
    apiClient
      .get<ApiResponse<PayerMapBundle>>(`/insurance/payers/${payerId}/map`)
      .then((r) => r.data.data),

  // Agency Command Center's Insurance Payers page — the full directory,
  // enriched with each payer's version status.
  listAllPayers: () =>
    apiClient
      .get<ApiResponse<DirectoryPayerSummary[]>>('/insurance/payers/directory/all')
      .then((r) => r.data.data),

  getPayerDirectoryEntry: (payerId: string) =>
    apiClient
      .get<ApiResponse<DirectoryPayer>>(`/insurance/payers/directory/${payerId}`)
      .then((r) => r.data.data),

  // Agency-only.
  updatePayerDirectoryEntry: (payerId: string, patch: PayerDirectoryPatch) =>
    apiClient
      .patch<ApiResponse<DirectoryPayer>>(`/insurance/payers/directory/${payerId}`, patch)
      .then((r) => r.data.data),

  // Agency-only — what deleting this payer would take with it, for a
  // confirm-dialog warning before committing to the cascade below.
  getPayerDeleteImpact: (payerId: string) =>
    apiClient
      .get<ApiResponse<PayerDeleteImpact>>(`/insurance/payers/directory/${payerId}/delete-impact`)
      .then((r) => r.data.data),

  // Agency-only. Cascades — removes the payer's location selections and
  // charting/map/knowledge history along with the directory entry itself.
  deletePayer: (payerId: string) =>
    apiClient
      .delete<ApiResponse<{ deleted: boolean }>>(`/insurance/payers/directory/${payerId}`)
      .then((r) => r.data.data),

  // Agency-only — the charting module's own already-gated endpoint, called
  // directly rather than duplicated in the setup module. The one remaining
  // manual step — synthesis itself is fully automatic (see backend
  // charting.service.ts).
  activateMapVersion: (versionId: string) =>
    apiClient
      .post<
        ApiResponse<PayerMapBundle['active']>
      >(`/insurance/charting/map-versions/${versionId}/activate`)
      .then((r) => r.data.data),

  // Agency-only manual charting run — places a real outbound call, no dedup
  // against existing charting history, and does NOT require the location to
  // have added this payer first (see backend listAgencyLocations). subscriber
  // is optional and never stored — see ChartingSubscriberInput.
  triggerCharting: (payerId: string, locationId: string, subscriber?: ChartingSubscriberInput) =>
    apiClient
      .post<
        ApiResponse<TriggerChartingResult>
      >(`/insurance/payers/${payerId}/charting/trigger`, { locationId, subscriber })
      .then((r) => r.data.data),

  // Agency-only — this agency's client locations, for the "Run charting"
  // picker on the payer detail page.
  listAgencyLocations: () =>
    apiClient
      .get<ApiResponse<AgencyLocation[]>>('/insurance/agency-locations')
      .then((r) => r.data.data),

  // Agency-only — full detail for one version (map, knowledge, rendered
  // text, source runs), regardless of whether it's active yet.
  getMapVersion: (versionId: string) =>
    apiClient
      .get<ApiResponse<MapVersionDetail>>(`/insurance/charting/map-versions/${versionId}`)
      .then((r) => r.data.data),

  // Agency-only — the charting run a version was built from, including its
  // call transcript.
  getChartingRun: (runId: string) =>
    apiClient
      .get<ApiResponse<ChartingRunDetail>>(`/insurance/charting/runs/${runId}`)
      .then((r) => r.data.data),

  // Agency-only — every charting call placed for this payer, newest first.
  listChartingRuns: (payerId: string) =>
    apiClient
      .get<ApiResponse<ChartingRunSummary[]>>('/insurance/charting/runs', { params: { payerId } })
      .then((r) => r.data.data),
};
